// Admin county-participation page. A single searchable list of every county in
// the region with an inline status picker that auto-saves on change (no global
// Save button, matching the rest of the admin surface). A summary strip up top
// shows per-status counts and doubles as a click-to-filter; the search box
// narrows by county name. Sorting toggles between alphabetical and grouped-by-
// status so sales can triage the funnel.
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Alert, CircularProgress, Stack, TextField, InputAdornment,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
  TableSortLabel, IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { fetchParticipation, setCountyStatus } from '../../store/admin-participation-slice.js';
import { formatDateTime } from '../../utils/admin-inquiry-format.js';
import { PARTICIPATION_STATUS_ORDER } from '../../config/participation.js';
import ParticipationStatsStrip from '../../components/admin/participation/ParticipationStatsStrip.jsx';
import ParticipationStatusSelect from '../../components/admin/participation/ParticipationStatusSelect.jsx';

const STATUS_RANK = PARTICIPATION_STATUS_ORDER.reduce((acc, s, i) => {
  acc[s] = i;
  return acc;
}, {});

const AdminParticipationPage = () => {
  const dispatch = useDispatch();
  const {
    items, counts, loading, error, saving, saveError,
  } = useSelector((s) => s.adminParticipation);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [sortBy, setSortBy] = useState('name'); // 'name' | 'status'

  useEffect(() => {
    dispatch(fetchParticipation());
  }, [dispatch]);

  const handleChange = (countyId, status) => {
    dispatch(setCountyStatus({ countyId, status }));
  };

  const toggleFilter = (status) => {
    setStatusFilter((prev) => (prev === status ? null : status));
  };

  const visibleItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = items.filter((it) => {
      if (statusFilter && it.status !== statusFilter) return false;
      if (term && !it.name.toLowerCase().includes(term)) return false;
      return true;
    });
    const sorted = [...filtered];
    if (sortBy === 'status') {
      sorted.sort((a, b) => (STATUS_RANK[a.status] - STATUS_RANK[b.status])
        || a.name.localeCompare(b.name));
    } else {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
    }
    return sorted;
  }, [items, search, statusFilter, sortBy]);

  if (loading && items.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress aria-label="Loading participation" />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          Participation
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Set each county&apos;s status in the statewide initiative. Changes save
          automatically and appear on the public form&apos;s participation map.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

      <Stack spacing={2}>
        <ParticipationStatsStrip
          counts={counts}
          activeFilter={statusFilter}
          onToggleFilter={toggleFilter}
        />

        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search counties…"
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton aria-label="Clear search" size="small" onClick={() => setSearch('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        {visibleItems.length === 0 ? (
          <Alert severity="info">No counties match your search.</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '60vh' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'name'}
                      direction="asc"
                      onClick={() => setSortBy('name')}
                    >
                      County
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'status'}
                      direction="asc"
                      onClick={() => setSortBy('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Last updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleItems.map((it) => (
                  <TableRow key={it.countyId} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{it.name}</TableCell>
                    <TableCell>
                      <ParticipationStatusSelect
                        countyId={it.countyId}
                        countyName={it.name}
                        status={it.status}
                        saving={saving.includes(it.countyId)}
                        onChange={handleChange}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {it.updatedAt ? formatDateTime(it.updatedAt) : '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
    </Box>
  );
};

export default AdminParticipationPage;
